/**
 * VILLE SAISIE DANS LES CRITÈRES → COMMUNE FRANÇAISE, vérifiée avant d'enregistrer.
 *
 * Même normalisation et mêmes paramètres que `supabase/functions/offres-recherche/geo.ts` (à garder
 * alignés) : l'écran voit la commune que la recherche retiendra. Sans cette vérification, une ville
 * inconnue (« Madrid ») faisait chercher dans toute la France sans rien dire, et une correspondance
 * approchée (« Londres ») cherchait autour de Saint-Martin-de-Londres (constaté le 17/10/2026).
 *
 * `indisponible` (réseau, API en panne) ne bloque pas : la recherche garde son comportement d'avant.
 */
export type ResolutionCommune =
  | { statut: 'exacte' | 'approchee'; nom: string; departement: string }
  | { statut: 'introuvable' }
  | { statut: 'indisponible' };

export function nomDeCommune(ville: string): string {
  return ville
    .replace(/\b\d{5}\b/g, '')
    .replace(/\b\d{1,2}\s*(e|er|ème|eme)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** « Saint-Étienne », « saint etienne » et « SAINT ETIENNE » sont la même commune. */
function comparable(nom: string): string {
  return nom
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[-'’]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export async function resoudreCommune(ville: string): Promise<ResolutionCommune> {
  const nom = nomDeCommune(ville);
  try {
    const p = new URLSearchParams({ nom, fields: 'nom,code,codeDepartement', boost: 'population', limit: '1' });
    const reponse = await fetch(`https://geo.api.gouv.fr/communes?${p.toString()}`);
    if (!reponse.ok) return { statut: 'indisponible' };
    const json: unknown = await reponse.json();
    const commune = Array.isArray(json) ? (json[0] as { nom?: unknown; codeDepartement?: unknown } | undefined) : undefined;
    if (!commune || typeof commune.nom !== 'string') return { statut: 'introuvable' };
    return {
      statut: comparable(commune.nom) === comparable(nom) ? 'exacte' : 'approchee',
      nom: commune.nom,
      departement: typeof commune.codeDepartement === 'string' ? commune.codeDepartement : '',
    };
  } catch {
    return { statut: 'indisponible' };
  }
}
